import React, { useState } from 'react';
import styled from 'styled-components';

export interface Header {
  key: string;
  label: string;
  editable?: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
  type?: 'string' | 'number' | 'date';
}

interface EditableTableProps {
  headers: Header[];
  data: any[];
  onDataChange?: (updatedData: any[]) => void;
}

const TableContainer = styled.div`
  overflow-x: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: white;
`;

const Th = styled.th<{ width?: string; align?: string }>`
  border-bottom: 1px solid #ddd;
  padding: 8px;
  text-align: ${({ align }) => align || 'left'};
  background-color: #f2f2f2;
  ${({ width }) => width && `width: ${width};`}
`;

const Td = styled.td<{ align?: string }>`
  border-bottom: 1px solid #ddd;
  padding: 8px;
  text-align: ${({ align }) => align || 'left'};
`;

const Input = styled.input<{ align?: string }>`
  width: 100%;
  padding: 5px;
  border: 1px solid #ccc;
  border-radius: 5px;
  text-align: ${({ align }) => align || 'left'};
`;

const EditableTable: React.FC<EditableTableProps> = ({
  headers,
  data,
  onDataChange,
}) => {
  const [tableData, setTableData] = useState(data);

  const handleChange = (
    rowIndex: number,
    key: string,
    value: string | number,
  ) => {
    const updatedData = [...tableData];
    updatedData[rowIndex] = { ...updatedData[rowIndex], [key]: value };
    setTableData(updatedData);
    if (onDataChange) {
      onDataChange(updatedData);
    }
  };

  const formatValue = (value: any, type?: string) => {
    if (value === null || value === undefined) return '';
    if (type === 'number') return Number(value).toLocaleString('pt-BR');
    if (type === 'date') {
      const date = new Date(value);
      return !isNaN(date.getTime()) ? date.toLocaleDateString('pt-BR') : value;
    }
    return value;
  };

  const handleDateInput = (value: string) => {
    // Remove qualquer caractere que não seja número
    const numericValue = value.replace(/\D/g, '');

    // Aplica a máscara dd/mm/yyyy
    const day = numericValue.slice(0, 2);
    const month = numericValue.slice(2, 4);
    const year = numericValue.slice(4, 8);

    let masked = day;
    if (month) masked += '/' + month;
    if (year) masked += '/' + year;

    return masked;
  };

  return (
    <TableContainer>
      <StyledTable>
        <thead>
          <tr>
            {headers.map((header) => (
              <Th
                key={header.key}
                width={header.width}
                align={header.align || 'left'}
              >
                {header.label}
              </Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tableData.map((row, rowIndex) => (
            <tr key={row.id || rowIndex}>
              {headers.map((header) => (
                <Td key={`${rowIndex}-${header.key}`} align={header.align}>
                  {header.editable ? (
                    <Input
                      type={header.type === 'number' ? 'number' : 'text'}
                      align={header.align}
                      value={String(row[header.key] ?? '')}
                      onChange={(e) => {
                        let inputValue = e.target.value;

                        if (header.type === 'date') {
                          inputValue = handleDateInput(inputValue);
                          handleChange(rowIndex, header.key, inputValue);
                        } else if (header.type === 'number') {
                          // Permite vazio como string ou número válido
                          const parsed =
                            inputValue === '' ? '' : Number(inputValue);
                          handleChange(rowIndex, header.key, parsed);
                        } else {
                          handleChange(rowIndex, header.key, inputValue);
                        }
                      }}
                    />
                  ) : (
                    formatValue(row[header.key], header.type)
                  )}
                </Td>
              ))}
            </tr>
          ))}
        </tbody>
      </StyledTable>
    </TableContainer>
  );
};

export default EditableTable;
